import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const StageDetailsModal = ({ stage, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'contacts', 'outputs', 'timeline'
  const [progress, setProgress] = useState(stage?.progress || 0);
  const [notes, setNotes] = useState('');

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success/10 text-success';
      case 'active':
        return 'bg-primary/10 text-primary';
      case 'pending':
        return 'bg-muted/10 text-muted-foreground';
      case 'delayed':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted/10 text-muted-foreground';
    }
  };

  const handleSave = () => {
    onUpdate({ ...stage, progress, notes });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-lg ${getStatusColor(stage?.status)}`}>
              <Icon name={stage?.icon} size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">{stage?.name}</h2>
              <p className="text-sm text-muted-foreground mt-1">{stage?.timing}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted/20 rounded-lg transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-6 pt-4 border-b border-border">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'overview' ?'bg-primary/10 text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'contacts' ?'bg-primary/10 text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Contacts ({stage?.contacts?.length + stage?.backupContacts?.length})
          </button>
          <button
            onClick={() => setActiveTab('outputs')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'outputs' ?'bg-primary/10 text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Required Outputs ({stage?.requiredOutputs?.length})
          </button>
          <button
            onClick={() => setActiveTab('timeline')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'timeline' ?'bg-primary/10 text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Timeline
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-280px)]">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Status and Progress */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted/5 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Status</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(stage?.status)}`}>
                    {stage?.status?.charAt(0)?.toUpperCase() + stage?.status?.slice(1)}
                  </span>
                </div>
                <div className="bg-muted/5 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Progress</p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-muted/20 rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium">{progress}%</span>
                  </div>
                </div>
              </div>

              {/* Objectives */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">Objectives</h3>
                <p className="text-sm text-muted-foreground">{stage?.objectives}</p>
              </div>

              {/* Progress Update */}
              {stage?.status === 'active' && (
                <div>
                  <label className="text-sm font-semibold text-foreground mb-2 block">
                    Update Progress
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={progress}
                    onChange={(e) => setProgress(parseInt(e?.target?.value))}
                    className="w-full h-2 bg-muted/20 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>0%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="text-sm font-semibold text-foreground mb-2 block">
                  Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e?.target?.value)}
                  placeholder="Add notes or updates about this stage..."
                  className="w-full h-32 px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
            </div>
          )}

          {activeTab === 'contacts' && (
            <div className="space-y-6">
              {/* Primary Contacts */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-4">Primary Contacts</h3>
                <div className="space-y-3">
                  {stage?.contacts?.map((contact, index) => (
                    <div key={index} className="bg-muted/5 p-4 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                            <Icon name="User" size={20} className="text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{contact?.name}</p>
                            <p className="text-xs text-muted-foreground">{contact?.role}</p>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Icon name="Mail" size={14} className="text-muted-foreground" />
                          <a href={`mailto:${contact?.email}`} className="text-primary hover:underline">
                            {contact?.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Icon name="Phone" size={14} className="text-muted-foreground" />
                          <a href={`tel:${contact?.phone}`} className="text-foreground">
                            {contact?.phone}
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Backup Contacts */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-4">Backup Contacts</h3>
                <div className="space-y-3">
                  {stage?.backupContacts?.map((contact, index) => (
                    <div key={index} className="bg-muted/5 p-4 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-secondary/10 rounded-full flex items-center justify-center">
                            <Icon name="User" size={20} className="text-secondary" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{contact?.name}</p>
                            <p className="text-xs text-muted-foreground">{contact?.role}</p>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Icon name="Mail" size={14} className="text-muted-foreground" />
                          <a href={`mailto:${contact?.email}`} className="text-primary hover:underline">
                            {contact?.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Icon name="Phone" size={14} className="text-muted-foreground" />
                          <a href={`tel:${contact?.phone}`} className="text-foreground">
                            {contact?.phone}
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'outputs' && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4">Required Deliverables</h3>
              <div className="space-y-3">
                {stage?.requiredOutputs?.map((output, index) => (
                  <div key={index} className="flex items-start gap-3 bg-muted/5 p-4 rounded-lg">
                    <div className="mt-0.5">
                      <Icon name="CheckCircle" size={18} className="text-success" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-foreground">{output}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="space-y-6">
              {stage?.startDate && (
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
                      <Icon name="Play" size={18} className="text-success" />
                    </div>
                    <div className="w-0.5 h-12 bg-border"></div>
                  </div>
                  <div className="flex-1 pb-6">
                    <p className="text-sm font-medium text-foreground">Start Date</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {new Date(stage?.startDate)?.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              )}

              {stage?.expectedCompletion && (
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="Target" size={18} className="text-primary" />
                    </div>
                    <div className="w-0.5 h-12 bg-border"></div>
                  </div>
                  <div className="flex-1 pb-6">
                    <p className="text-sm font-medium text-foreground">Expected Completion</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {new Date(stage?.expectedCompletion)?.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              )}

              {stage?.completedDate && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
                    <Icon name="CheckCircle" size={18} className="text-success" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">Completed</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {new Date(stage?.completedDate)?.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              )}

              {stage?.expectedStart && !stage?.startDate && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-muted/10 rounded-full flex items-center justify-center">
                    <Icon name="Clock" size={18} className="text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">Expected Start</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {new Date(stage?.expectedStart)?.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="default" onClick={handleSave}>
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StageDetailsModal;